<template>
  <main-template>
    <v-row>
      <v-col>
        <h2>
          <v-btn color="primary" x-small text @click="$router.back()">
            <v-icon left> fa fa-angle-left </v-icon>
            <span class="sr-only">Voltar</span>
          </v-btn>
          Pendências de Professores
          <span class="meu-componente-especial">
            <e-help-tooltip :text="$constants.tooltips.diarioOnline.pendenciasDeProfessores" />
          </span>
        </h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-autocomplete
          prepend-icon="mdi-school"
          v-model="escola"
          :items="this.franquiasList"
          item-text="descricao"
          item-value="id"
          dense
          return-object
          filled
          color="orange"
          label="Selecione uma escola"
          @change="loadPendenciasProfessores(escola.id)"
          :disabled="!podeTrocarFranquia"
        ></v-autocomplete>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="4" class="ml-auto">
        <div class="d-flex align-center">
          <v-label class="mt-6">Buscar:</v-label>
          <v-text-field v-model="search" append-icon="mdi-magnify" single-line hide-details>
          </v-text-field>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="professoresAgrupados.length > 0 && !carregandoPendencias">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <span class="text-h6">Pendências de Professores</span>
            <v-spacer></v-spacer>
            <v-chip color="green darken-2" dark small> Total: {{ totalProfessores }} </v-chip>
          </v-card-title>

          <v-card-text>
            <v-expansion-panels multiple>
              <professor-expansion-item
                v-for="professor in professoresPaginados"
                :key="professor.id"
                :professor="professor"
              />
            </v-expansion-panels>
          </v-card-text>
        </v-card>
        <e-paginate
          v-if="totalPages > 1"
          :current_page="currentPage"
          :last_page="totalPages"
          :page-number="currentPage"
          :search="searchTerm"
          @changeActionResponse="handlePageChange"
          class="mt-4"
        />
      </v-col>
    </v-row>

    <v-row v-else-if="!carregandoPendencias">
      <v-col cols="12">
        <v-alert type="info" text>
          Nenhuma pendência encontrada para a escola selecionada.
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-if="carregandoPendencias">
      <v-col cols="12">
        <div class="text-center mb-4">
          <v-icon color="primary" class="mb-2">mdi-loading mdi-spin</v-icon>
          <p class="text-body-2 text-medium-emphasis">Aguarde, isso pode demorar...</p>
        </div>

        <v-card class="mb-3" v-for="n in 3" :key="n" elevation="1">
          <v-card-text>
            <div class="d-flex align-center mb-3">
              <v-skeleton-loader type="avatar" class="me-3"></v-skeleton-loader>

              <div class="flex-grow-1">
                <v-skeleton-loader type="heading" class="mb-1" width="60%"></v-skeleton-loader>
                <v-skeleton-loader type="subtitle" width="40%"></v-skeleton-loader>
              </div>

              <v-skeleton-loader type="chip" width="80px"></v-skeleton-loader>
            </div>

            <v-skeleton-loader type="paragraph" class="mb-2"></v-skeleton-loader>

            <div class="d-flex justify-end gap-2">
              <v-skeleton-loader type="button" width="80px"></v-skeleton-loader>
              <v-skeleton-loader type="button" width="100px"></v-skeleton-loader>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import EPaginate from "@/components/EPaginate.vue";
import ProfessorExpansionItem from "./ProfessorExpansionItem.vue";

export default {
  components: {
    ProfessorExpansionItem,
    EPaginate,
  },

  computed: {
    ...mapGetters("Auth", ["user"]),

    professoresAgrupados() {
      if (!this.pendenciasList || !this.pendenciasList.turmas) {
        return [];
      }

      const professoresMap = new Map();

      this.pendenciasList.turmas.forEach((turma) => {
        turma.professores.forEach((professor) => {
          professor.gestoes.forEach((gestao) => {
            const professorNome = professor.nome;

            if (!professoresMap.has(professorNome)) {
              professoresMap.set(professorNome, {
                nome: professorNome,
                gestoes: [],
              });
            }

            professoresMap.get(professorNome).gestoes.push({
              id: gestao.id,
              turma: turma.descricao,
              disciplina_id: gestao.disciplina_id,
              disciplinas_da_gestao: gestao.disciplinas_da_gestao,
              relacaoAulasEtapas: gestao.relacaoAulasEtapas,
              pendencias: gestao.pendencias,
              turma_id: turma.id,
              instrutor_id: gestao.instrutor_id,
              is_polivalencia: gestao.is_polivalencia,
              series_da_turma: turma.series_da_turma.split(",").map((s) => s.trim()),
            });
          });
        });
      });

      return Array.from(professoresMap.values());
    },

    professoresFiltrados() {
      if (!this.search) {
        return this.professoresAgrupados;
      }

      const searchTerm = this.search.toLowerCase().trim();
      return this.professoresAgrupados.filter((professor) =>
        professor.nome.toLowerCase().includes(searchTerm)
      );
    },

    totalProfessores() {
      return this.professoresAgrupados.length;
    },

    totalPages() {
      return Math.ceil(this.totalProfessores / this.itemsPerPage);
    },

    professoresPaginados() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.professoresFiltrados.slice(start, end);
    },
  },

  async mounted() {
    await this.loadData();
    if (this.escola.id) {
      await this.loadPendenciasProfessores(this.escola.id);
    }
  },

  data() {
    return {
      escola: {
        id: null,
        descricao: null,
      },
      pendenciasList: [],
      carregandoPendencias: false,
      currentPage: 1,
      itemsPerPage: 5,
      search: "",
      searchTerm: "",
      franquiasList: [],
      podeTrocarFranquia: false,
    };
  },

  watch: {
    search() {
      this.currentPage = 1;
    },
  },

  methods: {
    async loadData() {
      this.franquiasList = await this.$services.franquiasService.listAll();
      this.escola.id = this.user.franquia.id;
      this.podeTrocarFranquia = this.user.permissions.some((p) => p === "franquias.*");
    },

    async loadPendenciasProfessores(escolaId) {
      if (!escolaId) return;

      this.carregandoPendencias = true;
      try {
        const response = await this.$services.gestoesDeAulasService.getProfessoresComPendencias(
          escolaId
        );
        this.pendenciasList = response;
        this.currentPage = 1;
        this.search = "";
      } catch (error) {
        this.pendenciasList = [];
        this.$handleError(error);
      } finally {
        this.carregandoPendencias = false;
      }
    },

    handlePageChange(page, searchTerm) {
      this.currentPage = page;
      // Removido this.searchTerm pois agora usamos this.search diretamente
    },
  },
};
</script>

<style scoped>
.v-expansion-panel-header {
  min-height: 48px;
}
</style>
